﻿import { useMemo, useState, type ReactNode } from 'react';
import { DndContext, closestCenter, type DragEndEvent, type SensorDescriptor } from '@dnd-kit/core';

import { AppHeader } from '../app/AppHeader';
import { ContextModeSelector, type ContextModeOption } from '../app/ContextModeSelector';
import { ContextStatus } from '../app/ContextStatus';
import { DesktopNav, type DesktopNavItem } from '../app/DesktopNav';
import {
  EmotionalStateSelector,
  type EmotionalStateFamilyGroup,
} from '../app/EmotionalStateSelector';
import { GuidelinePanel } from '../app/GuidelinePanel';
import { HeroPanel } from '../app/HeroPanel';
import { MissionList, type MissionListItem } from '../app/MissionList';
import { MobileNav, type MobileNavItem } from '../app/MobileNav';
import { Button } from '../core/Button';
import { ProgressIndicator } from '../core/ProgressIndicator';
import { SectionLabel } from '../core/SectionLabel';
import { EmptyState } from '../../components/States';
import { CATEGORY_ICON } from '../data/categoryIcons';
import { EMOTIONAL_FAMILY_PRESENTATION_LIST, getStatePresentationsOfFamily } from '../data/emotional-impact';
import { resolveImagery, type ImageKey } from '../data/imagery';
import { MODE_PRESENTATION_LIST } from '../data/modes';
import type { ResolvedPresentation } from '../data/presentation-types';
import type { UseAscendSessionResult } from '../session/useAscendSession';
import { fromContentData } from '../../../domain/routine/MissionContent';
import { TaskStatus } from '../../../domain/routine/enums';
import type { DailyStep } from '../../../domain/routine/models';
import type { DailyStepId } from '../../../domain/shared/ids';
import { getProgressMessageCode, type ProgressMessageCode } from '../../../domain/progression/CalculateDailyProgress';
import { isStepActiveForMode } from '../../../domain/routine/DayModeRules';
import { useMatchMedia } from '../session/useMatchMedia';
import { COMPACT_VIEWPORT_QUERY } from '../session/useMatchMedia';
import { useTranslation } from '../data/translations';
import { resolveMissionInstruction, resolveMissionObjective, resolveMissionTitle } from '../data/missionI18n';

/**
 * Aucune route dédiée n'existe encore pour Missions/Progression/Ressources (voir `routes.tsx` :
 * seuls `/`, `/today`, `/history`, `/settings`, `/architecture` existent) — ces entrées naviguent
 * donc en interne, par ancre, vers les sections déjà présentes sur cet écran. Le clic ne doit
 * jamais rester sans effet ; si une section est absente (ex. zone masquée par préférence), la
 * navigation ne fait simplement rien plutôt que de créer une page vide.
 */
const NAV_SECTION_ID: Record<string, string> = {
  aujourdhui: 'today-top',
  missions: 'missions-du-jour',
  progression: 'progression-quotidienne',
  ressources: 'ressources-editoriales',
};

export interface TodayScreenMissionRowContent extends Omit<MissionListItem, 'id'> {
  readonly id: DailyStepId;
}

export interface TodayScreenReorderProps {
  readonly active: boolean;
  readonly orderedIds: readonly DailyStepId[];
  readonly sensors: SensorDescriptor<object>[];
  readonly onStart: () => void;
  readonly onCancel: () => void;
  readonly onSave: () => void;
  readonly onMoveMission: (id: DailyStepId, direction: -1 | 1) => void;
  readonly onDragEnd: (event: DragEndEvent) => void;
}

export interface TodayScreenMissionsProps {
  readonly steps: readonly DailyStep[];
  readonly busyStepId: DailyStepId | undefined;
  readonly onStart: (step: DailyStep) => void;
  readonly onComplete: (step: DailyStep) => void;
  readonly onReset: (step: DailyStep) => void;
  readonly onRequestSkip: (step: DailyStep) => void;
  readonly onRequestPostpone: (step: DailyStep) => void;
}

export interface TodayScreenProps {
  readonly session: UseAscendSessionResult;
  readonly greeting: string;
  readonly dateLabel: string;
  readonly feedback: string;
  readonly missions: TodayScreenMissionsProps;
  readonly reorder: TodayScreenReorderProps;
  readonly modeDialogOpen: boolean;
  readonly onOpenModeDialog: () => void;
  readonly onCloseModeDialog: () => void;
  readonly emotionalStateDialogOpen: boolean;
  readonly onOpenEmotionalStateDialog: () => void;
  readonly onCloseEmotionalStateDialog: () => void;
  /** Contenu métier existant (inspiration, guidance adaptative, séance de sport) — inchangé. */
  readonly thematicContent?: ReactNode;
  /** Boîtes de dialogue existantes (ignorer, reporter, enregistrer l'ordre) — inchangées. */
  readonly dialogs?: ReactNode;
}

function buildModeOptions(t: (key: string) => string): readonly ContextModeOption<string>[] {
  return MODE_PRESENTATION_LIST.map((definition) => ({
    value: definition.mode,
    label: t(labelKeyToCatalogKey(definition.labelKey)),
    icon: definition.icon,
    scope: definition.scopeClass,
  }));
}

function buildEmotionalFamilies(
  t: (key: string) => string,
): readonly EmotionalStateFamilyGroup<string>[] {
  return EMOTIONAL_FAMILY_PRESENTATION_LIST.map((family) => ({
    key: family.family,
    label: t(labelKeyToCatalogKey(family.labelKey)),
    icon: family.icon,
    scope: family.scopeClass,
    states: getStatePresentationsOfFamily(family.family).map((state) => ({
      value: state.state,
      label: t(labelKeyToCatalogKey(state.labelKey)),
    })),
  }));
}

function heroImageFor(presentation: ResolvedPresentation): string {
  return resolveImagery(presentation.heroImage as ImageKey);
}

/** Convertit une labelKey du domaine (ex. `ascend.mode.normal.label`) en clé catalogue i18n (ex. `mode.normal`). */
function labelKeyToCatalogKey(labelKey: string): string {
  return labelKey.replace('ascend.', '').replace('.label', '').replace(/-/g, '_');
}

const PROGRESS_MESSAGE_KEY: Record<ProgressMessageCode, string> = {
  idle: 'progress.message.idle',
  launched: 'progress.message.launched',
  continuing: 'progress.message.continuing',
  advancing: 'progress.message.advancing',
  solid: 'progress.message.solid',
  ascended: 'progress.message.ascended',
};

function progressMessage(t: (key: string) => string, code: ProgressMessageCode): string {
  return t(PROGRESS_MESSAGE_KEY[code]);
}

/** Défile jusqu'à la section visée et lui donne le focus clavier ; ne fait rien si elle est absente. */
function scrollToSection(id: string, reducedMotion: boolean): boolean {
  const target = document.getElementById(id);
  if (!target) return false;
  target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
  target.focus({ preventScroll: true });
  return true;
}

function toMissionListItem(
  step: DailyStep,
  index: number,
  activeForMode: boolean,
  busy: boolean,
  presentation: ResolvedPresentation,
  handlers: TodayScreenMissionsProps,
  expandedId: DailyStepId | null,
  onToggleExpanded: (id: DailyStepId) => void,
  t: (key: string) => string,
): MissionListItem {
  const content = fromContentData(step.data);
  const isDone = step.status === TaskStatus.completed;
  const isDeferred = step.status === TaskStatus.postponed;
  const state = isDone ? 'done' : isDeferred ? 'deferred' : 'todo';
  const canSkipOrPostpone =
    step.status === TaskStatus.pending ||
    step.status === TaskStatus.inProgress ||
    step.status === TaskStatus.skipped;
  const secondaryActions = [
    ...(step.status === TaskStatus.pending
      ? [{ label: t('action.complete_direct'), onSelect: () => handlers.onComplete(step) }]
      : []),
    ...(step.status === TaskStatus.inProgress || step.status === TaskStatus.skipped
      ? [{ label: t('action.reset'), onSelect: () => handlers.onReset(step) }]
      : []),
    ...(step.status === TaskStatus.pending || step.status === TaskStatus.inProgress
      ? [{ label: t('action.skip'), onSelect: () => handlers.onRequestSkip(step), danger: true }]
      : []),
    ...(canSkipOrPostpone
      ? [{ label: t('action.postpone'), onSelect: () => handlers.onRequestPostpone(step) }]
      : []),
  ];
  const completeAllowed =
    step.status === TaskStatus.pending || step.status === TaskStatus.inProgress;
  const primary =
    step.status === TaskStatus.pending
      ? { label: t('action.start'), onSelect: () => handlers.onStart(step) }
      : step.status === TaskStatus.skipped
        ? { label: t('action.reactivate'), onSelect: () => handlers.onStart(step) }
        : step.status === TaskStatus.inProgress
          ? { label: t('action.complete'), onSelect: () => handlers.onComplete(step) }
          : step.status === TaskStatus.completed
            ? { label: t('action.reset'), onSelect: () => handlers.onReset(step) }
            : undefined;

  return {
    id: step.id,
    index: String(index + 1).padStart(2, '0'),
    icon: CATEGORY_ICON[step.category],
    category: t(`category.${step.category}`),
    title: resolveMissionTitle(step.title, t),
    time: step.suggestedTime ?? '',
    duration: `${step.estimatedDurationMinutes} min`,
    priority: step.essential || presentation.exigence === 'maximale' ? t('priority.essentiel') : t('priority.recommande'),
    state,
    activeForMode,
    busy,
    ...(step.postponedToDate ? { postponedLabel: `${t('status.postponed_label')} ${step.postponedToDate}` } : {}),
    ...(primary ? { actionLabel: primary.label, onAction: primary.onSelect } : {}),
    ...(completeAllowed ? { onComplete: () => handlers.onComplete(step), completeLabel: t('action.complete') } : {}),
    secondaryActions,
    expanded: expandedId === step.id,
    onToggleExpanded: () => onToggleExpanded(step.id),
    detail: content ? (
      <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
        <SectionLabel size={9} tone="muted">
          {t('mission.objective_label')}
        </SectionLabel>
        <p style={{ margin: 0, fontFamily: 'var(--font-ui)', fontSize: 14, color: 'var(--text-body)' }}>
          {resolveMissionObjective(step.title, t) || content.objective}
        </p>
        <SectionLabel size={9} tone="muted">
          {t('mission.instructions_label')}
        </SectionLabel>
        <ul style={{ margin: 0, paddingLeft: 18, fontFamily: 'var(--font-ui)', fontSize: 14, color: 'var(--text-body)' }}>
          {content.instructions.map((instruction, i) => (
            <li key={instruction}>{resolveMissionInstruction(step.title, i, t) || instruction}</li>
          ))}
        </ul>
        {content.optionalTip ? (
          <p style={{ margin: 0, fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--text-muted)' }}>
            {content.optionalTip}
          </p>
        ) : null}
      </div>
    ) : undefined,
  };
}

/**
 * Écran Aujourd'hui — unique implémentation responsive du Design System ASCEND V7. Composant
 * de présentation pur : reçoit `session` (issu de `useAscendSession`) et les actions métier
 * encore orchestrées par `TodayPage` (missions, réorganisation, dialogues, contenu thématique).
 * N'accède ni à Dexie, ni à un repository, ne recrée aucun use case.
 */
export function TodayScreen({
  session,
  greeting,
  dateLabel,
  feedback,
  missions,
  reorder,
  modeDialogOpen,
  onOpenModeDialog,
  onCloseModeDialog,
  emotionalStateDialogOpen,
  onOpenEmotionalStateDialog,
  onCloseEmotionalStateDialog,
  thematicContent,
  dialogs,
}: TodayScreenProps) {
  const { t } = useTranslation();
  const isCompact = useMatchMedia(COMPACT_VIEWPORT_QUERY);
  const [expandedStepId, setExpandedStepId] = useState<DailyStepId | null>(null);
  const [activeNavKey, setActiveNavKey] = useState<string>('aujourdhui');
  const presentation = session.resolvedPresentation;

  const NAV_ITEMS: readonly (MobileNavItem & DesktopNavItem)[] = [
    { key: 'aujourdhui', label: t('nav.today'), icon: 'normal' },
    { key: 'missions', label: t('nav.missions'), icon: 'focus' },
    { key: 'progression', label: t('nav.progression'), icon: 'elevation' },
    { key: 'ressources', label: t('nav.ressources'), icon: 'plus' },
  ];

  const modeOptions = useMemo(() => buildModeOptions(t), [t]);
  const emotionalFamilies = useMemo(() => buildEmotionalFamilies(t), [t]);

  const [selectedMode, setSelectedMode] = useState(session.mode ?? '');
  const [selectedEmotionalState, setSelectedEmotionalState] = useState<string | null>(
    session.emotionalState,
  );

  /** Logique métier unique de validation d'état — partagée par le bouton Valider (simple clic) et
   * le double-clic direct sur un état, pour ne jamais dupliquer la sauvegarde (voir revue « petites
   * corrections UX »). Reçoit la valeur explicitement plutôt que de lire `selectedEmotionalState`,
   * qui pourrait ne pas encore refléter un `onChange` tout juste déclenché par le même geste. */
  const confirmEmotionalState = (nextValue: string | null) => {
    void session.changeEmotionalState(nextValue as never);
    onCloseEmotionalStateDialog();
  };

  /** Même principe pour le mode : logique unique partagée par Valider et le double-clic direct
   * (voir revue « ajustements UX / navigation / carte sport »). */
  const confirmMode = (nextValue: string) => {
    void session.changeMode(nextValue as never);
    onCloseModeDialog();
  };

  const handleNavigate = (key: string) => {
    const id = NAV_SECTION_ID[key];
    if (!id) return;
    const scrolled = scrollToSection(id, session.accessibility.reducedMotion);
    if (scrolled) setActiveNavKey(key);
  };

  if (!presentation || !session.routineDetails) {
    return null;
  }

  const missionRows = missions.steps.map((step, index) =>
    toMissionListItem(
      step,
      index,
      isStepActiveForMode(step, session.routineDetails!.routine.dayMode),
      missions.busyStepId === step.id,
      presentation,
      missions,
      expandedStepId,
      (id) => setExpandedStepId((current) => (current === id ? null : id)),
      t,
    ),
  );

  /** Traduit l'intention et les routines via les clés du catalogue (labelKeyToCatalogKey).
   *  Les données métier (emotional-impact.ts) fournissent intentionKey et routineKeys ;
   *  on résout chaque clé à travers le catalogue i18n pour obtenir le texte localisé. */
  const translatedIntention = t(labelKeyToCatalogKey(presentation.intentionKey));
  const translatedRoutines = presentation.routineKeys.map((key) => t(labelKeyToCatalogKey(key)));

  const guidelinesDesktop = (
    <GuidelinePanel
      intention={translatedIntention}
      routines={translatedRoutines}
      style={{
        height: '100%',
        borderRadius: 'var(--radius-panel)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    />
  );
  const guidelinesCompact = (
    <GuidelinePanel
      intention={translatedIntention}
      routines={translatedRoutines}
      style={{ padding: '18px var(--gutter-mobile) 20px' }}
    />
  );

  const contextControls = (
    <ContextStatus
      mode={session.mode ?? undefined}
      modeIcon={presentation.icon}
      state={presentation.state ? presentation.state : null}
      stateIcon={presentation.emotionalIcon ?? undefined}
      size={isCompact ? 'sm' : 'md'}
      onOpenMode={onOpenModeDialog}
      modeExpanded={modeDialogOpen}
      modeControls="mode-selector-dialog"
      modePending={session.modeChangePending}
      onOpenState={onOpenEmotionalStateDialog}
      stateExpanded={emotionalStateDialogOpen}
      stateControls="emotional-state-selector-dialog"
      statePending={session.emotionalStateChangePending}
    />
  );

  const missionListNode = (
    <MissionList
      label={t('missions.label')}
      actionLabel={reorder.active ? t('missions.reorganiser') : t('missions.reorganiser')}
      missions={missionRows}
      variant={isCompact ? 'mobile' : 'desktop'}
      onAction={reorder.active ? undefined : reorder.onStart}
      reordering={reorder.active}
      onMoveMission={(id, direction) => reorder.onMoveMission(id as DailyStepId, direction)}
      showTimes={!presentation.hiddenByUser.includes('durations')}
      dimPriorities={presentation.dimmed.includes('priorities')}
      dimDurations={presentation.dimmed.includes('durations')}
    />
  );

  return (
    <main
      className={['today-screen', ...presentation.scopes].join(' ')}
      style={{
        display: 'grid',
        gap: isCompact ? 12 : 'var(--stack-block)',
        width: '100%',
        maxWidth: isCompact ? '100%' : 'var(--shell-max)',
        margin: '0 auto',
        padding: isCompact ? '14px var(--gutter-mobile) 96px' : '22px var(--gutter-desktop) 40px',
        boxSizing: 'border-box',
      }}
    >
      {/* Mobile : padding horizontal aligné sur la gouttière (--gutter-mobile = 16px)
          pour une cohérence visuelle avec les autres blocs. Desktop inchangé (22px). */}
      <AppHeader
        id="today-top"
        tabIndex={-1}
        date={dateLabel}
        markIcon={presentation.icon}
        {...(isCompact ? { style: { padding: '14px var(--gutter-mobile)' } as const } : {})}
      />

      {!isCompact && (
        <DesktopNav
          items={NAV_ITEMS}
          active={activeNavKey}
          onNavigate={handleNavigate}
          aria-label="Navigation des sections"
          trailing={contextControls}
        />
      )}

      <HeroPanel
        date={dateLabel.toUpperCase()}
        greeting={greeting}
        mantra={t(labelKeyToCatalogKey(presentation.thoughtKey))}
        image={heroImageFor(presentation)}
        imagePosition={presentation.heroImagePosition}
        compact={isCompact}
        height={isCompact ? 240 : 264}
        aside={isCompact ? undefined : guidelinesDesktop}
      />
      {isCompact && guidelinesCompact}

      {/* Mobile : MODE / ÉTAT au-dessus de la progression (plus dans la bottom nav).
          Revue Android 412px : les badges étaient trop éloignés de la zone de contexte
          dans la seconde ligne du bottom nav ; remontés ici, juste après les Lignes
          directrices, ils sont visuellement liés au hero et à la progression.
          Revue Android finale : le groupe MODE + ÉTAT est centré horizontalement
          (justifyContent: center) comme un bloc unique. */}
      {isCompact && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {contextControls}
        </div>
      )}

      {!presentation.hiddenByUser.includes('progress') && (
        <div
          id="progression-quotidienne"
          tabIndex={-1}
          className={presentation.dimmed.includes('progress') ? 'ai-dimmed' : ''}
        >
          <ProgressIndicator
            value={session.progress?.completedCount ?? 0}
            total={session.progress?.activeCount ?? missionRows.length}
            label={progressMessage(t, getProgressMessageCode(session.progress?.percentage ?? 0))}
            accessibleLabel={t('progress.label')}
            compact={isCompact}
          />
        </div>
      )}

      <div id="missions-du-jour" tabIndex={-1}>
        {reorder.active ? (
          <DndContext sensors={reorder.sensors} collisionDetection={closestCenter} onDragEnd={reorder.onDragEnd}>
            {missionListNode}
          </DndContext>
        ) : missions.steps.length === 0 ? (
          <EmptyState
            title={t('missions.empty_title')}
            message={t('missions.empty_message')}
          />
        ) : (
          missionListNode
        )}
      </div>

      {reorder.active ? (
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={reorder.onCancel}>
            {t('action.annuler')}
          </Button>
          <Button variant="primary" onClick={reorder.onSave}>
            {t('action.enregistrer')}
          </Button>
        </div>
      ) : null}

      {!presentation.hiddenByUser.includes('editorial-cards') && thematicContent ? (
        <div
          id="ressources-editoriales"
          tabIndex={-1}
          className={presentation.dimmed.includes('editorial-cards') ? 'ai-dimmed' : ''}
          style={{
            display: 'grid',
            gridTemplateColumns: isCompact ? '1fr' : 'repeat(3, minmax(0, 1fr))',
            alignItems: 'stretch',
            gap: isCompact ? 14 : 18,
            marginTop: isCompact ? 6 : 10,
          }}
        >
          {thematicContent}
        </div>
      ) : null}

      <p className="sr-live" role="status" aria-live="polite">
        {feedback}
      </p>

      {isCompact && (
        <MobileNav
          items={NAV_ITEMS}
          active={activeNavKey}
          onNavigate={handleNavigate}
          aria-label="Navigation des sections"
        />
      )}

      {dialogs}

      {modeDialogOpen ? (
        <ModeDialog
          options={modeOptions}
          value={selectedMode}
          onChange={setSelectedMode}
          onClose={onCloseModeDialog}
          onConfirm={() => confirmMode(selectedMode)}
          onCommit={(value) => {
            setSelectedMode(value);
            confirmMode(value);
          }}
          pending={session.modeChangePending}
          t={t}
        />
      ) : null}

      {emotionalStateDialogOpen ? (
        <EmotionalStateDialog
          families={emotionalFamilies}
          value={selectedEmotionalState}
          onChange={setSelectedEmotionalState}
          onClose={onCloseEmotionalStateDialog}
          onConfirm={() => confirmEmotionalState(selectedEmotionalState)}
          onCommit={(value) => {
            setSelectedEmotionalState(value);
            confirmEmotionalState(value);
          }}
          pending={session.emotionalStateChangePending}
          t={t}
        />
      ) : null}
    </main>
  );
}

interface ModeDialogProps {
  readonly options: readonly ContextModeOption<string>[];
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
  readonly onCommit: (value: string) => void;
  readonly pending: boolean;
  readonly t: (key: string) => string;
}

function ModeDialog({ options, value, onChange, onClose, onConfirm, onCommit, pending, t }: ModeDialogProps) {
  return (
    <div
      id="mode-selector-dialog"
      role="dialog"
      aria-modal="true"
      aria-label={t('mode.changer')}
      style={{
        position: 'fixed',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        background: 'rgba(8,9,10,.72)',
        zIndex: 10,
      }}
    >
      <div
        style={{
          background: 'var(--surface-panel)',
          border: '1px solid var(--accent-line)',
          borderRadius: 'var(--radius-panel)',
          padding: 24,
          maxWidth: 520,
          width: '92%',
          boxShadow: 'var(--shadow-lift)',
        }}
      >
        <ContextModeSelector options={options} value={value} onChange={onChange} onCommit={onCommit} />
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            {t('mode.cancel')}
          </Button>
          <Button variant="primary" onClick={onConfirm} disabled={pending}>
            {pending ? t('mode.pending') : t('mode.confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}

interface EmotionalStateDialogProps {
  readonly families: readonly EmotionalStateFamilyGroup<string>[];
  readonly value: string | null;
  readonly onChange: (value: string | null) => void;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
  readonly onCommit: (value: string | null) => void;
  readonly pending: boolean;
  readonly t: (key: string) => string;
}

function EmotionalStateDialog({
  families,
  value,
  onChange,
  onClose,
  onConfirm,
  onCommit,
  pending,
  t,
}: EmotionalStateDialogProps) {
  return (
    <div
      id="emotional-state-selector-dialog"
      role="dialog"
      aria-modal="true"
      aria-label={t('state.declarer')}
      style={{
        position: 'fixed',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        background: 'rgba(8,9,10,.72)',
        zIndex: 10,
      }}
    >
      <div
        style={{
          background: 'var(--surface-panel)',
          border: '1px solid var(--accent-line)',
          borderRadius: 'var(--radius-panel)',
          padding: 24,
          maxWidth: 520,
          width: '92%',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: 'var(--shadow-lift)',
        }}
      >
        <EmotionalStateSelector families={families} value={value} onChange={onChange} onCommit={onCommit} />
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            {t('state.cancel')}
          </Button>
          <Button variant="primary" onClick={onConfirm} disabled={pending}>
            {pending ? t('state.pending') : t('state.confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}
